import { getToken, onMessage } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, messagingReady } from './init-firebase';

// Firebase Cloud Messaging public VAPID key
// This should be generated in Firebase Console -> Project Settings -> Cloud Messaging
const VAPID_KEY = 'BP6Buu9yhIUNuhvm2GzuVlD9P2qiteM8ZnmGZJ2TqjKrKu_eJyXSJ4AuH9qWbt9_DRoDosWWPKCSU4b7r90_tHU'; // TODO: Replace with actual VAPID key

/**
 * Push messaging support check. getMessaging() synchronously touches
 * navigator.serviceWorker, which does not exist in iOS in-app webviews,
 * Safari Private Browsing, or Lockdown Mode — calling it there throws.
 * Mirrors the guard in utils/init-firebase.js.
 */
function isPushSupported() {
    return (
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'PushManager' in window
    );
}

/**
 * Request notification permission from the user
 * @returns {Promise<{success: boolean, permission: string}>}
 */
export async function requestNotificationPermission() {
    try {
        const permission = await Notification.requestPermission();
        return {
            success: permission === 'granted',
            permission,
        };
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return {
            success: false,
            permission: 'denied',
            error: error.message,
        };
    }
}

/**
 * Get FCM token and save it to user document
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, token: string|null}>}
 */
export async function getFCMToken(userId) {
    try {
        // Check if notifications are supported
        if (!('Notification' in window)) {
            console.warn('This browser does not support notifications');
            return { success: false, token: null, error: 'Notifications not supported' };
        }

        // Check if permission is granted
        if (Notification.permission !== 'granted') {
            console.log('Notification permission not granted');
            return { success: false, token: null, error: 'Permission not granted' };
        }

        if (!isPushSupported()) {
            console.warn('Push messaging not supported in this browser');
            return { success: false, token: null, error: 'Push not supported' };
        }

        // Resolves null when firebase's own isSupported() probe fails (e.g.
        // indexedDB restrictions) — never call getMessaging() directly, it
        // throws unhandled rejections in unsupported browsers.
        const messaging = await messagingReady;
        if (!messaging) {
            console.warn('Push messaging not supported in this browser');
            return { success: false, token: null, error: 'Push not supported' };
        }

        // Get FCM token
        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
        });

        if (token) {
            console.log('FCM Token obtained:', token.substring(0, 20) + '...');

            // Save token to user document (deduplicate)
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                'FCM Tokens': arrayUnion(token),
                lastTokenUpdate: new Date(),
            });

            return { success: true, token };
        } else {
            console.warn('No FCM token obtained');
            return { success: false, token: null, error: 'No token generated' };
        }
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return { success: false, token: null, error: error.message };
    }
}

/**
 * Set up foreground message listener
 * @param {Function} onMessageReceived - Callback when message received
 * @returns {Function} Unsubscribe function
 */
export function setupForegroundMessageListener(onMessageReceived) {
    try {
        if (!isPushSupported()) {
            return () => {}; // push unsupported — no-op unsubscribe
        }

        // messagingReady resolves async; keep the sync unsubscribe contract.
        let unsubscribe = () => {};
        let disposed = false;
        messagingReady.then((messaging) => {
            if (!messaging || disposed) return;
            unsubscribe = onMessage(messaging, (payload) => {
            console.log('Foreground message received:', payload);

            // Extract notification data
            const notificationTitle = payload.notification?.title || payload.data?.title || 'Notification';
            const notificationBody = payload.notification?.body || payload.data?.body || '';
            const notificationLink = payload.data?.link || '/';

            if (onMessageReceived) {
                onMessageReceived({
                    title: notificationTitle,
                    body: notificationBody,
                    link: notificationLink,
                    payload,
                });
            }

            // Show browser notification if document not focused
            if (document.hidden) {
                new Notification(notificationTitle, {
                    body: notificationBody,
                    icon: '/fhl-logo.png',
                    badge: '/fhl_logo_icon.ico',
                    data: { link: notificationLink },
                });
            }
            });
        });

        return () => {
            disposed = true;
            unsubscribe();
        };
    } catch (error) {
        console.error('Error setting up foreground message listener:', error);
        return () => {}; // Return no-op unsubscribe
    }
}

/**
 * Check if user has granted notification permission
 * @returns {boolean}
 */
export function hasNotificationPermission() {
    if (!('Notification' in window)) {
        return false;
    }
    return Notification.permission === 'granted';
}

/**
 * Check if user has been asked for notification permission before
 * @returns {boolean}
 */
export function hasAskedForNotificationPermission() {
    return localStorage.getItem('notificationPermissionAsked') === 'true';
}

/**
 * Mark that user has been asked for notification permission
 */
export function markNotificationPermissionAsked() {
    localStorage.setItem('notificationPermissionAsked', 'true');
}

/**
 * Initialize FCM for a user
 * Requests permission and saves token
 * @param {string} userId - User ID
 * @returns {Promise<{success: boolean, token: string|null}>}
 */
export async function initializeFCM(userId) {
    try {
        // Request permission
        const permissionResult = await requestNotificationPermission();
        
        if (!permissionResult.success) {
            return { success: false, token: null, error: 'Permission denied' };
        }

        // Get and save token
        const tokenResult = await getFCMToken(userId);
        
        // Mark as asked
        markNotificationPermissionAsked();

        return tokenResult;
    } catch (error) {
        console.error('Error initializing FCM:', error);
        return { success: false, token: null, error: error.message };
    }
}

